import Club from '@src/data/club'
import { ExerciseResponse } from '@src/data/exercise-response'
import WorkoutData from '@src/data/workout-data'
import { WorkoutExercise } from '@src/data/workout-exercise'
import { NO_EQUIPMENT } from '@src/util/constants'

export type WorkoutPlannerMode = 'replace' | 'append'

export interface WorkoutPlannerConstraints {
  goal?: string
  body_area?: string[]
  equipment?: string[]
  requires_equipment?: boolean
  health_limitation?: string[]
  focus?: string[]
}

export interface WorkoutPlannerRequest {
  exercises: ExerciseResponse[]
  club: Club | null
  workout: WorkoutData
  target_duration_minutes: number
  workout_name?: string
  mode?: WorkoutPlannerMode
  allow_repeated_exercises?: boolean
  constraints?: WorkoutPlannerConstraints
  current_duration_seconds?: number
}

export interface WorkoutSelection {
  exercise_id: number
  name: string
  count: number
  duration_seconds: number
  total_duration_seconds: number
  has_sides: boolean
  equipment: string[]
  tags: string[]
}

export interface WorkoutPlannerResult {
  success: boolean
  target_duration_seconds: number
  actual_duration_seconds: number
  duration_shortfall_seconds: number
  tolerance_seconds: number
  total_exercise_count: number
  unique_exercise_count: number
  available_unique_exercise_count: number
  repeat_fill_possible: boolean
  selections: WorkoutSelection[]
  excluded_for_health: number
  failure_reason?: 'filtered_pool_exhausted' | 'duration_tolerance_not_met'
  error?: string
  warnings: string[]
}

interface Candidate {
  exercise: ExerciseResponse
  duration: number
  score: number
}

function normalizeTag (value: string): string {
  return (value || '').toString().trim().toLowerCase().replace(/\s+/g, '_').replace(/\//g, '_')
}

function normalizeTags (values?: string[]): string[] {
  if (!Array.isArray(values)) return []
  return values
    .map(value => normalizeTag(value))
    .filter(value => !!value && value !== 'no_limitations' && value !== 'none')
}

function getExerciseTags (exercise: ExerciseResponse, tagType?: string): string[] {
  const tags = Array.isArray(exercise.exerciseTags) ? exercise.exerciseTags : []
  return tags
    .filter(tag => tag.type !== 'ExerciseTags::Level' && (!tagType || tag.type === tagType))
    .map(tag => normalizeTag(tag.key))
}

function hasAny (available: string[], requested: string[]): boolean {
  if (!requested.length) return true
  return requested.some(tag => available.indexOf(tag) !== -1)
}

function getExerciseDuration (exercise: ExerciseResponse, club: Club | null, workout: WorkoutData, warnings: string[]): number {
  const workoutExercise = new WorkoutExercise()
  workoutExercise.setExercise(exercise)

  if (club) {
    return workoutExercise.getExerciseDuration(club, workout.type, workout.circleWorkout)
  }

  if (exercise.durationMedium) {
    warnings.push('Club duration settings were unavailable, so exercise durationMedium was used.')
    return exercise.durationMedium
  }

  warnings.push('Club duration settings were unavailable, so a 45 second fallback duration was used.')
  return 45
}

function getToleranceSeconds (targetDurationMinutes: number): number {
  return targetDurationMinutes <= 20 ? 60 : 120
}

function isHealthCompatible (exercise: ExerciseResponse, healthLimitations: string[]): boolean {
  if (!healthLimitations.length) return true
  const notAllowed = getExerciseTags(exercise, 'ExerciseTags::HealthLimitation')
  return !healthLimitations.some(limit => notAllowed.indexOf(limit) !== -1)
}

function getEquipmentTags (exercise: ExerciseResponse): string[] {
  const equipment = Array.isArray(exercise.equipment) ? exercise.equipment.map(item => normalizeTag(item)) : []
  return equipment.length ? equipment : [NO_EQUIPMENT]
}

function matchesHardConstraints (exercise: ExerciseResponse, constraints: WorkoutPlannerConstraints): boolean {
  const equipment = normalizeTags(constraints.equipment)
  const bodyArea = normalizeTags(constraints.body_area)
  const focus = normalizeTags(constraints.focus)

  const exerciseEquipment = Array.isArray(exercise.equipment) ? exercise.equipment : []
  if (constraints.requires_equipment && !exerciseEquipment.length) return false
  if (!hasAny(getEquipmentTags(exercise), equipment)) return false
  if (!hasAny(getExerciseTags(exercise, 'ExerciseTags::BodyArea'), bodyArea)) return false
  if (!hasAny(getExerciseTags(exercise, 'ExerciseTags::Focus'), focus)) return false

  return true
}

function scoreExercise (exercise: ExerciseResponse, constraints: WorkoutPlannerConstraints): number {
  let score = 0
  if (hasAny(getExerciseTags(exercise, 'ExerciseTags::BodyArea'), normalizeTags(constraints.body_area))) score += 3
  if (hasAny(getExerciseTags(exercise, 'ExerciseTags::Focus'), normalizeTags(constraints.focus))) score += 2
  // candidates always match the requested equipment (hard constraint, or no equipment requested for the fallback pool)
  /* istanbul ignore else */
  if (hasAny(getEquipmentTags(exercise), normalizeTags(constraints.equipment))) score += 2
  return score
}

function shuffleCandidates (candidates: Candidate[]): Candidate[] {
  const shuffled = candidates.slice()
  for (let index = shuffled.length - 1; index > 0; index--) {
    const swapIndex = Math.floor(Math.random() * (index + 1))
    const current = shuffled[index]
    shuffled[index] = shuffled[swapIndex]
    shuffled[swapIndex] = current
  }
  return shuffled
}

function buildSelectionsFromCounts (counts: { [exerciseId: number]: number }, candidates: Candidate[]): WorkoutSelection[] {
  return candidates
    .filter(candidate => counts[candidate.exercise.id] > 0)
    .map(candidate => {
      const exercise = candidate.exercise
      const count = counts[exercise.id]
      return {
        exercise_id: exercise.id,
        name: exercise.getName(),
        count,
        duration_seconds: candidate.duration,
        total_duration_seconds: candidate.duration * count,
        has_sides: !!exercise.hasSides,
        equipment: getEquipmentTags(exercise),
        tags: getExerciseTags(exercise)
      }
    })
}

function getTotalCapacitySeconds (candidates: Candidate[]): number {
  return candidates.reduce((sum, candidate) => {
    return sum + candidate.duration * (candidate.exercise.hasSides ? 2 : 1)
  }, 0)
}

export function planWorkout (request: WorkoutPlannerRequest): WorkoutPlannerResult {
  const warnings: string[] = []
  const constraints = request.constraints || {}
  const allowRepeatedExercises = !!request.allow_repeated_exercises
  const targetDurationMinutes = Number(request.target_duration_minutes)
  const hasValidTargetDuration = isFinite(targetDurationMinutes) && targetDurationMinutes > 0
  const targetDurationSeconds = hasValidTargetDuration
    ? Math.max(1, Math.round(targetDurationMinutes * 60))
    : 0
  const desiredAdditionalSeconds = request.mode === 'append'
    ? Math.max(1, targetDurationSeconds - (request.current_duration_seconds || 0))
    : targetDurationSeconds
  const toleranceSeconds = hasValidTargetDuration ? getToleranceSeconds(targetDurationMinutes) : 0
  const healthLimitations = normalizeTags(constraints.health_limitation)

  if (!hasValidTargetDuration) {
    return {
      success: false,
      target_duration_seconds: targetDurationSeconds,
      actual_duration_seconds: request.current_duration_seconds || 0,
      duration_shortfall_seconds: 0,
      tolerance_seconds: toleranceSeconds,
      total_exercise_count: 0,
      unique_exercise_count: 0,
      available_unique_exercise_count: 0,
      repeat_fill_possible: false,
      selections: [],
      excluded_for_health: 0,
      failure_reason: 'duration_tolerance_not_met',
      error: 'target_duration_minutes must be a positive number.',
      warnings
    }
  }

  let excludedForHealth = 0
  const compatibleExercises = (request.exercises || []).filter(exercise => {
    const compatible = isHealthCompatible(exercise, healthLimitations)
    if (!compatible) excludedForHealth++
    return compatible
  })

  let constrainedExercises = compatibleExercises.filter(exercise => matchesHardConstraints(exercise, constraints))
  const hasEquipmentConstraint = !!constraints.requires_equipment || normalizeTags(constraints.equipment).length > 0
  if (!constrainedExercises.length && compatibleExercises.length && !hasEquipmentConstraint) {
    warnings.push('No exercises matched every requested tag, so the planner used the filtered compatible pool.')
    constrainedExercises = compatibleExercises
  }

  const existingExerciseIds = new Set<number>(
    request.mode === 'append' && request.workout && Array.isArray(request.workout.workoutExercises)
      ? request.workout.workoutExercises.map(workoutExercise => workoutExercise.exercise.id)
      : []
  )
  const candidates = constrainedExercises
    .map(exercise => {
      return {
        exercise,
        duration: getExerciseDuration(exercise, request.club, request.workout, warnings),
        score: scoreExercise(exercise, constraints)
      }
    })
    .filter(candidate => candidate.duration > 0)
    .filter(candidate => allowRepeatedExercises || !existingExerciseIds.has(candidate.exercise.id))

  const randomizedCandidates = shuffleCandidates(candidates)
  const repeatFillPossible = randomizedCandidates.length > 0

  if (!randomizedCandidates.length) {
    const actualDurationSeconds = request.current_duration_seconds || 0
    return {
      success: false,
      target_duration_seconds: targetDurationSeconds,
      actual_duration_seconds: actualDurationSeconds,
      duration_shortfall_seconds: Math.max(0, targetDurationSeconds - actualDurationSeconds),
      tolerance_seconds: toleranceSeconds,
      total_exercise_count: 0,
      unique_exercise_count: 0,
      available_unique_exercise_count: 0,
      repeat_fill_possible: false,
      selections: [],
      excluded_for_health: excludedForHealth,
      failure_reason: 'filtered_pool_exhausted',
      error: 'No compatible exercises are available for the requested workout.',
      warnings
    }
  }

  const selectedCandidates: Candidate[] = []
  const counts: { [exerciseId: number]: number } = {}
  let plannedSeconds = 0

  for (const candidate of randomizedCandidates) {
    const increment = candidate.exercise.hasSides ? 2 : 1
    const incrementDuration = candidate.duration * increment
    const wouldBeSeconds = plannedSeconds + incrementDuration
    const currentDiff = Math.abs(desiredAdditionalSeconds - plannedSeconds)
    const nextDiff = Math.abs(desiredAdditionalSeconds - wouldBeSeconds)
    const needsMoreDuration = selectedCandidates.length === 0 ||
      plannedSeconds < desiredAdditionalSeconds - toleranceSeconds

    if (needsMoreDuration &&
        (wouldBeSeconds <= desiredAdditionalSeconds + toleranceSeconds || nextDiff < currentDiff)) {
      selectedCandidates.push(candidate)
      counts[candidate.exercise.id] = increment
      plannedSeconds = wouldBeSeconds
    }

    if (plannedSeconds >= desiredAdditionalSeconds - toleranceSeconds) break
  }

  if (allowRepeatedExercises && plannedSeconds < desiredAdditionalSeconds - toleranceSeconds) {
    let repeatIndex = 0
    const maxIterations = Math.max(1, Math.ceil((desiredAdditionalSeconds + toleranceSeconds) / Math.max(1, Math.min(...randomizedCandidates.map(candidate => candidate.duration))))) + randomizedCandidates.length

    for (let iteration = 0; iteration < maxIterations && plannedSeconds < desiredAdditionalSeconds - toleranceSeconds; iteration++) {
      const candidate = randomizedCandidates[repeatIndex % randomizedCandidates.length]
      repeatIndex++

      const increment = candidate.exercise.hasSides ? 2 : 1
      const incrementDuration = candidate.duration * increment
      const wouldBeSeconds = plannedSeconds + incrementDuration
      const currentDiff = Math.abs(desiredAdditionalSeconds - plannedSeconds)
      const nextDiff = Math.abs(desiredAdditionalSeconds - wouldBeSeconds)

      if (wouldBeSeconds <= desiredAdditionalSeconds + toleranceSeconds || nextDiff < currentDiff) {
        // the first pass already tried every candidate with a smaller planned duration, and a candidate it skipped
        // cannot fit once more time is planned, so every candidate picked here already has a count
        /* istanbul ignore if */
        if (!counts[candidate.exercise.id]) selectedCandidates.push(candidate)
        counts[candidate.exercise.id] = (counts[candidate.exercise.id] || /* istanbul ignore next */ 0) + increment
        plannedSeconds = wouldBeSeconds
      }
    }
  }

  const actualDurationSeconds = (request.mode === 'append' ? (request.current_duration_seconds || 0) : 0) + plannedSeconds
  const durationDiff = Math.abs(targetDurationSeconds - actualDurationSeconds)
  const durationShortfallSeconds = Math.max(0, targetDurationSeconds - actualDurationSeconds)
  const selections = buildSelectionsFromCounts(counts, selectedCandidates)
  const success = durationDiff <= toleranceSeconds

  return {
    success,
    target_duration_seconds: targetDurationSeconds,
    actual_duration_seconds: actualDurationSeconds,
    duration_shortfall_seconds: durationShortfallSeconds,
    tolerance_seconds: toleranceSeconds,
    total_exercise_count: selections.reduce((sum, selection) => sum + selection.count, 0),
    unique_exercise_count: selections.length,
    available_unique_exercise_count: randomizedCandidates.length,
    repeat_fill_possible: repeatFillPossible && (allowRepeatedExercises || getTotalCapacitySeconds(randomizedCandidates) < desiredAdditionalSeconds - toleranceSeconds),
    selections,
    excluded_for_health: excludedForHealth,
    failure_reason: success
      ? undefined
      : durationShortfallSeconds > toleranceSeconds
        ? 'filtered_pool_exhausted'
        : 'duration_tolerance_not_met',
    error: success ? undefined : 'Could not build a workout within the allowed duration tolerance.',
    warnings
  }
}
